import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { SubscriptionModal, SubscriptionPlan as UISubscriptionPlan } from '@pisaka/ui';
import { useSubscription } from './useSubscription';
import { socket } from '../../helpers/socket';
import { fetchCurrentSubscription as fetchCurrentSubscriptionRedux } from './state/controller';
import { AppStateTypes } from '../../store/appStateTypes';
import { AppDispatch } from '../../store/configureStore';
import { axiosInstance } from '../../helpers/axiosInstance';
import { useAuth } from '../auth/useAuth';
import { useTranslation } from 'react-i18next';

interface SubscriptionContainerProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  context?: 'premium' | 'tokens' | 'all';
}

export const SubscriptionContainer: React.FC<SubscriptionContainerProps> = ({
  isOpen,
  onClose,
  onSuccess,
  context = 'all',
}) => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const { token } = useSelector((state: AppStateTypes) => state.auth);

  const {
    plans,
    isLoading,
    error,
    fetchPlans,
    fetchCurrentSubscription,
    createSubscription,
    purchasePremium,
    clearError,
    currentSubscription,
  } = useSubscription();

  // Загружаем планы и текущую подписку при открытии модалки
  useEffect(() => {
    if (isOpen) {
      fetchPlans();
      fetchCurrentSubscription();
    }
  }, [isOpen, fetchPlans, fetchCurrentSubscription]);

  // Очищаем ошибку при закрытии модалки
  useEffect(() => {
    if (!isOpen) {
      clearError();
    }
  }, [isOpen, clearError]);

  const formatDuration = (days: number): string => {
    if (days === 30 || days === 31) return t('pricing.card.duration.month_1');
    if (days === 90 || days === 92) return t('pricing.card.duration.months_3');
    if (days === 180 || days === 183) return t('pricing.card.duration.months_6');
    if (days === 365 || days === 366) return t('pricing.card.duration.year_1');
    if (days >= 36500) return t('pricing.card.duration.forever');
    return t('pricing.card.duration.days', { count: days });
  };

  const allPlans = plans
    .map((plan) => ({
      id: plan.id,
      name: plan.name,
      price: Number(plan.price),
      durationDays: plan.durationDays,
      duration: formatDuration(plan.durationDays),
      features: plan.features || [],
      tokens: plan.tokens || 0,
      isPopular: plan.name === 'Стандарт',
    }))
    .filter((plan) => {
      if (context === 'premium') return !plan.tokens || plan.tokens === 0;
      if (context === 'tokens') return plan.tokens && plan.tokens > 0;
      return true;
    });

  const handleSubscribe = async (planId: string) => {
    try {
      const selectedPlan = plans.find((plan) => plan.id.toString() === planId);

      const paymentResult = await purchasePremium(planId);

      if (paymentResult?.paymentConfirmationToken && paymentResult?.paymentId && token) {
        // Проверяем подключение socket.io
        console.log('🔌 Socket.io connected:', socket.connected);
        console.log('🔌 Subscribing to WebSocket event:', `payment:${paymentResult.paymentId}`);

        // Если не подключен, подключаемся
        if (!socket.connected) {
          console.log('⚠️ Socket not connected, connecting...');
          socket.connect();
        }

        const handlePaymentSuccess = async (data?: any) => {
          await Promise.all([
            dispatch(fetchCurrentSubscriptionRedux(token)),
            fetchCurrentSubscription(),
          ]);
          onSuccess?.();
          socket.off(`payment:${paymentResult.paymentId}`, handlePaymentSuccess);
        };

        socket.on(`payment:${paymentResult.paymentId}`, handlePaymentSuccess);

        // @ts-ignore
        const checkout = new window.YooMoneyCheckoutWidget({
          confirmation_token: paymentResult.paymentConfirmationToken,
          customization: {
            modal: true
          },
          error_callback() {
            console.error('YooKassa widget initialization error');
          }
        });

        // Закрываем текущую модалку чтобы ЮКасса была поверх
        onClose();

        checkout.render();

        checkout.on('success', () => {
          checkout.destroy();
          handlePaymentSuccess();
        });

        checkout.on('fail', () => {
          socket.off(`payment:${paymentResult.paymentId}`, handlePaymentSuccess);
          checkout.destroy();
        });
      } else if (paymentResult?.status === 'succeeded') {
        // Платеж прошел успешно
        onSuccess?.();
        onClose();
      }
    } catch (err) {
      // Ошибка уже обрабатывается в хуке
      console.error('Subscription creation failed:', err);
    }
  };

  // Обработчик покупки токенов
  const handleTokenPurchase = async (planId: string, includePremium: boolean, totalPrice: number) => {
    if (!token) return;

    try {
      const response = await axiosInstance(token).post('/api/pricing/purchase', {
        planId,
        includePremium,
        totalPrice,
      });

      if (response.data.paymentConfirmationToken && response.data.paymentId) {
        if (!socket.connected) socket.connect();

        const handlePaymentSuccess = async () => {
          await Promise.all([
            dispatch(fetchCurrentSubscriptionRedux(token)),
            fetchCurrentSubscription(),
          ]);
          onSuccess?.();
          socket.off(`payment:${response.data.paymentId}`, handlePaymentSuccess);
        };

        socket.on(`payment:${response.data.paymentId}`, handlePaymentSuccess);

        // @ts-ignore
        const checkout = new window.YooMoneyCheckoutWidget({
          confirmation_token: response.data.paymentConfirmationToken,
          customization: { modal: true },
          error_callback() { console.error('YooKassa widget error'); },
        });

        onClose();
        checkout.render();

        checkout.on('success', () => {
          checkout.destroy();
          handlePaymentSuccess();
        });
        checkout.on('fail', () => {
          socket.off(`payment:${response.data.paymentId}`, handlePaymentSuccess);
          checkout.destroy();
        });
      }
    } catch (error) {
      console.error('Token purchase error:', error);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      onClose();
    }
  };


  // Проверяем есть ли у пользователя активная премиум подписка
  const hasPremium = currentSubscription?.plan?.features?.includes('pdf_export') || false;

  const featureLabels = {
    pdf_export: t('pricing.featureLabels.pdf_export'),
    double_page: t('pricing.featureLabels.double_page'),
    no_ads: t('pricing.featureLabels.no_ads'),
  };

  const planCardTexts = {
    popular: t('pricing.card.popular'),
    forDuration: (duration: string) => duration === t('pricing.card.duration.forever') ? duration : t('pricing.card.forDuration', { duration }),
    getDuration: formatDuration,
    whatsIncluded: t('pricing.card.whatsIncluded'),
    getTokenDescription: (k: number) => t('pricing.card.tokenDescription', { k }),
    getAddBasic: (price: number) => t('pricing.card.addBasic', { price }),
    processing: t('pricing.card.processing'),
    buy: t('pricing.card.buy'),
    comingSoon: t('pricing.card.comingSoon'),
  };

  return (
    <SubscriptionModal
      isOpen={isOpen}
      onClose={handleClose}
      onSubscribe={handleSubscribe}
      onTokenPurchase={handleTokenPurchase}
      plans={allPlans}
      featureLabels={featureLabels}
      hasPremium={hasPremium}
      texts={{ title: t('subscription.title'), choosePlan: t('subscription.choosePlan') }}
      planCardTexts={planCardTexts}
    />
  );
};
